import * as React from "react";
import { Refine } from "@refinedev/core";
import routerProvider from "@refinedev/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Outlet, Route, Routes } from "react-router";
import { Sidebar } from "@/components/Sidebar";
import { AuthGuard } from "@/components/AuthGuard";
import { GlobalErrorHandler } from "@/components/GlobalErrorHandler";
import { RoleRedirect } from "@/components/RoleRedirect";
import { authProvider } from "@/providers/authProvider";
import { dataProvider } from "@/providers/dataProvider";
import { accessControlProvider } from "@/providers/accessControlProvider";
import { notificationProvider, ToastContainer } from "@/providers/notificationProvider";
import { LoginPage } from "@/pages/auth/login";
import { UsersList } from "@/pages/users/list";
import { UsersShow } from "@/pages/users/show";
import { OperatorsList } from "@/pages/operators/list";
import { OperatorsEdit } from "@/pages/operators/edit";
import { AuditList } from "@/pages/audit/list";
import { PlacesList } from "@/pages/places/list";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

function PlaceholderPage({ title }: { title: string }): React.JSX.Element {
  return (
    <div className="flex items-center justify-center flex-1 p-6">
      <p className="text-[var(--muted)] text-[15px]">
        {title} — Раздел в разработке
      </p>
    </div>
  );
}

function AppLayout(): React.JSX.Element {
  return (
    <div className="flex min-h-screen bg-[var(--bg)]">
      <Sidebar />
      <main className="flex-1 ml-[240px] flex flex-col min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}

function NotFound(): React.JSX.Element {
  return (
    <div className="flex items-center justify-center flex-1 p-6">
      <p className="text-[var(--muted)] text-[15px]">Страница не найдена</p>
    </div>
  );
}

export function App(): React.JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Refine
          authProvider={authProvider}
          dataProvider={dataProvider}
          accessControlProvider={accessControlProvider}
          notificationProvider={notificationProvider}
          routerProvider={routerProvider}
          resources={[
            {
              name: "users",
              list: "/users",
              show: "/users/show/:id",
            },
            {
              name: "entitlement",
              show: "/users/show/:id",
              meta: { hide: true },
            },
            {
              name: "operators",
              list: "/operators",
              edit: "/operators/edit/:id",
            },
            {
              name: "audit",
              list: "/audit",
            },
            {
              name: "places",
              list: "/places",
            },
          ]}
          options={{
            syncWithLocation: true,
            warnWhenUnsavedChanges: false,
            disableTelemetry: true,
          }}
        >
          <GlobalErrorHandler />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              element={
                <AuthGuard>
                  <AppLayout />
                </AuthGuard>
              }
            >
              <Route
                path="/users"
                element={
                  <RoleRedirect resource="users">
                    <UsersList />
                  </RoleRedirect>
                }
              />
              <Route
                path="/users/show/:id"
                element={
                  <RoleRedirect resource="users" action="show">
                    <UsersShow />
                  </RoleRedirect>
                }
              />
              <Route
                path="/operators"
                element={
                  <RoleRedirect resource="operators">
                    <OperatorsList />
                  </RoleRedirect>
                }
              />
              <Route
                path="/operators/edit/:id"
                element={
                  <RoleRedirect resource="operators" action="edit">
                    <OperatorsEdit />
                  </RoleRedirect>
                }
              />
              <Route
                path="/audit"
                element={
                  <RoleRedirect resource="audit">
                    <AuditList />
                  </RoleRedirect>
                }
              />
              <Route
                path="/places"
                element={
                  <RoleRedirect resource="places">
                    <PlacesList />
                  </RoleRedirect>
                }
              />
              <Route
                path="/moderation"
                element={<PlaceholderPage title="Модерация" />}
              />
              <Route
                path="/dashboard"
                element={<PlaceholderPage title="Дашборд" />}
              />
              <Route path="/" element={<UsersList />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
          <ToastContainer />
        </Refine>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
